import { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  X, ArrowLeft, ArrowRight, Plus, Trash2, Check,
  ToggleLeft, ToggleRight, Printer, FileText, ChevronRight,
  HeartPulse, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { useAuthStore, ROL } from '@/store/auth.store';
import { Cie11Search, type Cie11Result } from '@/components/cie11/Cie11Search';
import { ComboboxInput } from '@/components/ui/ComboboxInput';
import { VademecumPickerInline, type MedicamentoSeleccionado } from '@/components/vademecum/VademecumPicker';
import {
  TAMANO_INFO, DISENO_INFO, getDimensiones,
  type TemplateData,
} from '../configuracion/templates/template-types';
import { TemplateRenderer } from '../configuracion/templates/TemplateRenderer';
import { usePlantillasConfig, DEFAULT_PLANTILLAS_CONFIG } from '../configuracion/templates/usePlantillaConfig';
import { getErrorMessage } from '@/lib/errors';

// ─── Types ───────────────────────────────────────────────────────────────────

interface MedItem {
  vademecumId?: string;
  /** Denominación genérica (principio activo) — obligatoria por COFEPRIS / LGS */
  denominacionGenerica: string;
  /** Denominación distintiva (nombre comercial/marca) — opcional */
  denominacionDistintiva: string;
  formaFarmaceutica: string;
  concentracion: string;
  viaAdministracion: string;
  dosis: string;
  frecuencia: string;
  duracion: string;
  indicaciones: string;
}

interface WizardForm {
  citaId: string;
  cie11: Cie11Result | null;
  indicaciones: string;
  notas: string;
  medicaciones: MedItem[];
  medActivosInactivar: Set<string>;
}

type StepId = 'contexto' | 'cabecera' | 'receta-previa' | 'farmacos' | 'med-activos' | 'resumen';

const STEP_LABEL: Record<StepId, string> = {
  'contexto':     'Contexto',
  'cabecera':     'Cabecera',
  'receta-previa':'Receta previa',
  'farmacos':     'Fármacos',
  'med-activos':  'Activos',
  'resumen':      'Resumen',
};

// ─── Shared helpers ───────────────────────────────────────────────────────────

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

const DOSIS_RAPIDAS = ['1/2 tableta', '1 tableta', '2 tabletas', '1 cápsula', '2 cápsulas', '5 ML', '10 ML', '1 gota', '2 gotas', '1 ampolleta'];
const FRECUENCIAS   = ['cada 4 horas', 'cada 6 horas', 'cada 8 horas', 'cada 12 horas', 'cada 24 horas', 'una vez al día', 'dos veces al día'];
const DURACIONES    = ['3 días', '5 días', '7 días', '10 días', '14 días', '21 días', '30 días', 'uso continuo'];

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

function toDisplayName(p: any): string {
  if (!p) return '';
  return [p.nombre, p.apellidoPaterno, p.apellidoMaterno].filter(Boolean).join(' ');
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  pacienteId: string;
  recetaId?: string | null;
  paciente?: any;
  citaId?: string;
  onClose: () => void;
}

const emptyForm: WizardForm = {
  citaId: '',
  cie11: null,
  indicaciones: '',
  notas: '',
  medicaciones: [],
  medActivosInactivar: new Set(),
};

const emptyMed: MedItem = {
  vademecumId: '', denominacionGenerica: '', denominacionDistintiva: '',
  formaFarmaceutica: '', concentracion: '', viaAdministracion: '',
  dosis: '', frecuencia: '', duracion: '', indicaciones: '',
};

export function RecetaWizard({ pacienteId, recetaId: initialRecetaId, paciente, citaId: initialCitaId, onClose }: Props) {
  const { usuario } = useAuthStore();
  const [stepIdx,          setStepIdx]          = useState(() => initialCitaId && !initialRecetaId ? 1 : 0);
  const [form,             setForm]             = useState<WizardForm>({ ...emptyForm, citaId: initialCitaId || '', medActivosInactivar: new Set() });
  const [medForm,          setMedForm]          = useState<MedItem>({ ...emptyMed });
  const [savedId,          setSavedId]          = useState<string | null>(initialRecetaId || null);
  const [showTratamientos, setShowTratamientos] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  // Fetch existing receta if editing a draft
  const { data: existingReceta } = useQuery({
    queryKey: ['receta', initialRecetaId],
    queryFn: () => api.get(`/api/recetas/${initialRecetaId}`).then(r => r.data),
    enabled: !!initialRecetaId,
  });

  useEffect(() => {
    if (existingReceta) {
      setForm(prev => ({
        ...prev,
        citaId: existingReceta.citaId || '',
        cie11: existingReceta.codigoCie11 ? {
          id:      existingReceta.idEntidadCie11 || '',
          theCode: existingReceta.codigoCie11,
          title:   existingReceta.tituloCie11 || '',
        } : null,
        indicaciones: existingReceta.indicaciones || '',
        notas: existingReceta.notas || '',
        medicaciones: (existingReceta.medicaciones || []).map((m: any) => ({
          vademecumId: m.vademecumId || '',
          denominacionGenerica: m.denominacionGenerica,
          denominacionDistintiva: m.denominacionDistintiva || '',
          formaFarmaceutica: m.formaFarmaceutica || '',
          concentracion: m.concentracion || '',
          viaAdministracion: m.viaAdministracion || '',
          dosis: m.dosis || '',
          frecuencia: m.frecuencia || '',
          duracion: m.duracion || '',
          indicaciones: m.indicaciones || '',
        })),
      }));
    }
  }, [existingReceta]);

  // Fetch recent citas for the patient
  const { data: citas = [] } = useQuery({
    queryKey: ['citas', pacienteId],
    queryFn: () => api.get('/api/citas', { params: { pacienteId } }).then(r => r.data as any[]),
    staleTime: 60_000,
  });

  // Fetch last active receta
  const { data: recetasActivas = [] } = useQuery({
    queryKey: ['recetas-activas', pacienteId],
    queryFn: () => api.get(`/api/pacientes/${pacienteId}/recetas`, { params: { status: 'activa' } }).then(r => r.data as any[]),
    staleTime: 30_000,
  });
  const prevActiva = recetasActivas[0] || null;

  // Fetch active medications
  const { data: medActivos = [] } = useQuery({
    queryKey: ['medicamentos-activos', pacienteId],
    queryFn: () => api.get(`/api/pacientes/${pacienteId}/medicamentos-activos`).then(r => r.data as any[]),
  });

  // Fetch treatment templates for suggestions
  const { data: tratamientos = [] } = useQuery({
    queryKey: ['tratamientos'],
    queryFn: () => api.get('/api/tratamientos').then(r => r.data as any[]),
    staleTime: 60_000,
  });

  // Fetch clinic info
  const { data: clinica } = useQuery({
    queryKey: ['clinica', usuario?.clinicaId],
    queryFn: () => api.get(`/api/clinicas/${usuario!.clinicaId}`).then(r => r.data),
    enabled: !!usuario?.clinicaId,
  });

  // Fetch medico's own logo (only for médico roles)
  const esMedicoRol = !!usuario?.rolId && ([ROL.MEDICO, ROL.MEDICO_ADMIN] as string[]).includes(usuario.rolId);
  const { data: medicoMe } = useQuery({
    queryKey: ['medico-me'],
    queryFn: () => api.get('/api/medicos/me').then(r => r.data),
    enabled: esMedicoRol,
  });

  const efectivoLogoUrl: string | undefined =
    medicoMe?.logoPropio || (clinica as any)?.logoUrl || undefined;

  const { data: plantillasConfig } = usePlantillasConfig();
  const plantillaCfg = (plantillasConfig ?? DEFAULT_PLANTILLAS_CONFIG).receta;

  // Build steps dynamically
  const steps: StepId[] = [
    'contexto', 'cabecera',
    ...(prevActiva && !initialRecetaId ? ['receta-previa' as StepId] : []),
    'farmacos', 'med-activos', 'resumen',
  ];
  const currentStep = steps[stepIdx];
  const isLast = stepIdx === steps.length - 1;

  // Mutations
  const saveMutation = useMutation({
    mutationFn: (dto: any) => savedId
      ? api.patch(`/api/recetas/${savedId}`, dto).then(r => r.data)
      : api.post('/api/recetas', dto).then(r => r.data),
    onSuccess: (data) => { if (data?.id) setSavedId(data.id); },
  });

  const activarMutation = useMutation({
    mutationFn: (recetaId: string) => api.post(`/api/recetas/${recetaId}/activar`, {
      medActivosInactivar: [...form.medActivosInactivar],
    }).then(r => r.data),
    onSuccess: () => { toast.success('Receta emitida'); onClose(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al emitir la receta')),
  });

  function buildDto(status?: string) {
    return {
      pacienteId,
      clinicaId: usuario?.clinicaId,
      medicoId: usuario?.id,
      citaId: form.citaId || null,
      codigoCie11:    form.cie11?.theCode  || null,
      tituloCie11:    form.cie11?.title    || null,
      idEntidadCie11: form.cie11?.id       || null,
      indicaciones: form.indicaciones || null,
      notas: form.notas || null,
      status: status || 'borrador',
      medicaciones: form.medicaciones.map(m => ({
        vademecumId: m.vademecumId || null,
        denominacionGenerica: m.denominacionGenerica,
        denominacionDistintiva: m.denominacionDistintiva || null,
        formaFarmaceutica: m.formaFarmaceutica || null,
        concentracion: m.concentracion || null,
        viaAdministracion: m.viaAdministracion || null,
        dosis: m.dosis || null,
        frecuencia: m.frecuencia || null,
        duracion: m.duracion || null,
        indicaciones: m.indicaciones || null,
      })),
    };
  }

  async function saveDraft() {
    try {
      await saveMutation.mutateAsync(buildDto('borrador'));
      toast.success('Guardado como borrador');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al guardar borrador'));
    }
  }

  async function handleEmitir() {
    try {
      const dto = buildDto('borrador');
      let id = savedId;
      if (!id) {
        const created = await saveMutation.mutateAsync(dto);
        id = created.id;
      } else {
        await saveMutation.mutateAsync(dto);
      }
      if (id) activarMutation.mutate(id);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al guardar antes de emitir'));
    }
  }

  function addMed() {
    if (!medForm.denominacionGenerica.trim()) { toast.error('El nombre del medicamento es requerido'); return; }
    setForm(prev => ({ ...prev, medicaciones: [...prev.medicaciones, { ...medForm }] }));
    setMedForm({ ...emptyMed });
  }

  function applyTratamiento(t: any) {
    if (!t.medicamentos?.length) { toast.error('Esta plantilla no tiene medicamentos'); return; }
    const newMeds: MedItem[] = t.medicamentos.map((m: any) => ({
      vademecumId:          m.vademecumId || '',
      denominacionGenerica: m.nombre,
      denominacionDistintiva: '',
      formaFarmaceutica: '',
      concentracion:     '',
      viaAdministracion: m.via || '',
      dosis:             m.dosis || '',
      frecuencia:        m.frecuencia || '',
      duracion:          m.duracion || '',
      indicaciones:      '',
    }));
    setForm(prev => ({ ...prev, medicaciones: [...prev.medicaciones, ...newMeds] }));
    toast.success(`${newMeds.length} medicamento${newMeds.length !== 1 ? 's' : ''} agregado${newMeds.length !== 1 ? 's' : ''} de "${t.nombre}"`);
  }

  function removeMed(i: number) {
    setForm(prev => ({ ...prev, medicaciones: prev.medicaciones.filter((_, j) => j !== i) }));
  }

  function toggleInactivar(medId: string) {
    setForm(prev => {
      const s = new Set(prev.medActivosInactivar);
      s.has(medId) ? s.delete(medId) : s.add(medId);
      return { ...prev, medActivosInactivar: s };
    });
  }

  function validateStep(): boolean {
    if (currentStep === 'cabecera' && !form.cie11) {
      toast.error('Selecciona un diagnóstico del CIE-11');
      return false;
    }
    if (currentStep === 'farmacos' && form.medicaciones.length === 0) {
      toast.error('Agrega al menos un fármaco');
      return false;
    }
    return true;
  }

  function next() { if (validateStep()) setStepIdx(i => Math.min(i + 1, steps.length - 1)); }
  function prev() { setStepIdx(i => Math.max(i - 1, 0)); }

  // Print preview
  const { width: pw, height: ph, cssSize } = getDimensiones(plantillaCfg.tamanoHoja, plantillaCfg.orientacion);
  const PREVIEW_SCALE = 0.55;
  const previewW = Math.round(pw * PREVIEW_SCALE);
  const previewH = Math.round(ph * PREVIEW_SCALE);

  const recetaFolio = existingReceta?.folio;
  const templateData: TemplateData = {
    tipo: 'receta',
    folio: recetaFolio || '—',
    fecha: new Date().toLocaleDateString('es-MX'),
    qrUrl: recetaFolio ? `${window.location.origin}/receta-digital/${recetaFolio}` : undefined,
    clinica: {
      nombre: (clinica as any)?.nombre || 'Clínica',
      cedula: (clinica as any)?.cedulaFiscal || '',
      direccion: (clinica as any)?.direccion || '',
      telefono: (clinica as any)?.telefono || '',
      logoUrl: efectivoLogoUrl,
    },
    medico: {
      nombre: (usuario as any)?.nombre || 'Médico',
      cedula: (usuario as any)?.cedula || '',
      especialidad: (usuario as any)?.especialidad || '',
    },
    paciente: {
      nombre: toDisplayName(paciente),
      fechaNacimiento: paciente?.fechaNacimiento ? formatDate(paciente.fechaNacimiento) : '',
      sexo: paciente?.genero ? ({ masculino: 'Masculino', femenino: 'Femenino', otro: 'Otro' }[paciente.genero as string] || '') : '',
    },
    fechaConsulta: new Date().toLocaleDateString('es-MX'),
    medicaciones: form.medicaciones.map(m => ({
      denominacionGenerica: m.denominacionGenerica,
      denominacionDistintiva: m.denominacionDistintiva || undefined,
      dosis: m.dosis,
      frecuencia: m.frecuencia,
      duracion: m.duracion,
      viaAdministracion: m.viaAdministracion || undefined,
      indicaciones: m.indicaciones,
    })),
    instrucciones: form.notas || undefined,
  };

  function handlePrint() {
    const el = printRef.current;
    if (!el) return;
    const css = `*{margin:0;padding:0;box-sizing:border-box}body{background:#fff;font-family:Arial,sans-serif}@media print{@page{size:${cssSize};margin:0}}`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><style>${css}</style></head><body>${DOMPurify.sanitize(el.innerHTML)}</body></html>`);
    w.document.close();
    w.onload = () => { w.print(); };
  }

  // ─── Step content ───────────────────────────────────────────────────────────

  function renderStep() {
    switch (currentStep) {
      case 'contexto': return (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div>
              <p className="text-xs text-text-secondary mb-1">Clínica</p>
              <p className="text-sm font-medium text-text-primary">{(clinica as any)?.nombre || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary mb-1">Fecha de creación</p>
              <p className="text-sm font-medium text-text-primary">{new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary mb-1">Médico</p>
              <p className="text-sm font-medium text-text-primary">{(usuario as any)?.nombre || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary mb-1">Paciente</p>
              <p className="text-sm font-medium text-text-primary">{toDisplayName(paciente)}</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Cita asociada <span className="text-text-secondary font-normal">(opcional)</span></label>
            <select value={form.citaId} onChange={e => setForm(p => ({ ...p, citaId: e.target.value }))} className={inputCls}>
              <option value="">Sin cita asociada</option>
              {(citas as any[])
                .sort((a, b) => new Date(b.fechaInicio).getTime() - new Date(a.fechaInicio).getTime())
                .slice(0, 15)
                .map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {formatDate(c.fechaInicio)} — {c.motivo || 'Consulta'}
                  </option>
                ))}
            </select>
          </div>
        </div>
      );

      case 'cabecera': return (
        <div className="space-y-5">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Diagnóstico CIE-11 *
            </label>
            <Cie11Search
              value={form.cie11}
              onChange={v => setForm(p => ({ ...p, cie11: v }))}
              placeholder="Buscar diagnóstico (mín. 3 caracteres)..."
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Nota clínica <span className="font-normal">(opcional)</span>
            </label>
            <textarea
              rows={2}
              className={`${inputCls} resize-none`}
              value={form.indicaciones}
              onChange={e => setForm(p => ({ ...p, indicaciones: e.target.value }))}
              placeholder="Observación adicional, estadio, contexto clínico..."
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Instrucciones al paciente <span className="font-normal">(opcional)</span></label>
            <textarea
              rows={2}
              className={`${inputCls} resize-none`}
              value={form.notas}
              onChange={e => setForm(p => ({ ...p, notas: e.target.value }))}
              placeholder="Reposo, dieta, restricciones..."
            />
          </div>
        </div>
      );

      case 'receta-previa': return (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-amber-700 mb-3">Existe una receta activa previa. Al emitir esta receta, la anterior quedará inactiva automáticamente.</p>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-text-primary">{prevActiva.folio}</span>
                <span className="text-xs text-text-secondary">{formatDate(prevActiva.fecha)}</span>
              </div>
              {prevActiva.indicaciones && <p className="text-xs text-text-secondary">{prevActiva.indicaciones}</p>}
              {prevActiva.medicaciones?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {prevActiva.medicaciones.map((m: any, i: number) => (
                    <span key={i} className="bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded-full border border-amber-200">
                      {m.denominacionGenerica}{m.dosis ? ` · ${m.dosis}` : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );

      case 'farmacos': return (
        <div className="space-y-5">
          {/* Treatment templates */}
          {tratamientos.length > 0 && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setShowTratamientos(p => !p)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100/70 transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  <HeartPulse size={14} className="text-primary-500" />
                  <span className="text-sm font-medium text-text-primary">Plantillas de tratamiento</span>
                  <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">{tratamientos.length}</span>
                </div>
                {showTratamientos ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
              </button>
              {showTratamientos && (
                <div className="divide-y divide-gray-50 max-h-52 overflow-y-auto">
                  {(tratamientos as any[]).map(t => (
                    <div key={t.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50/50">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-text-primary truncate">{t.nombre}</p>
                        <p className="text-xs text-text-secondary mt-0.5">
                          {t.medicamentos?.length
                            ? `${t.medicamentos.length} medicamento${t.medicamentos.length !== 1 ? 's' : ''}`
                            : 'Sin medicamentos'}
                          {t.descripcion ? ` · ${t.descripcion}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => applyTratamiento(t)}
                        disabled={!t.medicamentos?.length}
                        className="flex-shrink-0 ml-3 px-3 py-1.5 text-xs font-medium bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors border border-primary-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Aplicar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Add form */}
          <div className="border border-dashed border-primary-200 rounded-xl p-4 space-y-3 bg-primary-50/30">
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Agregar fármaco</p>
            <VademecumPickerInline
              medicamentosActivos={form.medicaciones.map(m => m.denominacionGenerica)}
              onSelect={(sel: MedicamentoSeleccionado) => {
                setMedForm(p => ({
                  ...p,
                  vademecumId: sel.vademecumId,
                  denominacionGenerica: sel.nombreGenerico,
                  denominacionDistintiva: sel.nombreComercial,
                  formaFarmaceutica: sel.formaFarmaceutica,
                  concentracion: sel.concentracion,
                  viaAdministracion: sel.viaAdministracion,
                  dosis: sel.dosisSugerida,
                  frecuencia: sel.frecuenciaSugerida,
                }));
              }}
            />
            <div>
              <label className="block text-xs text-text-secondary mb-1">Denominación genérica (nombre del medicamento) *</label>
              <input className={inputCls} value={medForm.denominacionGenerica}
                onChange={e => setMedForm(p => ({ ...p, denominacionGenerica: e.target.value }))}
                placeholder="o escribe manualmente" />
            </div>
            {medForm.denominacionDistintiva && (
              <div>
                <label className="block text-xs text-text-secondary mb-1">Denominación distintiva (nombre comercial)</label>
                <input className={inputCls} value={medForm.denominacionDistintiva}
                  onChange={e => setMedForm(p => ({ ...p, denominacionDistintiva: e.target.value }))}
                  placeholder="Nombre de marca" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Forma farmacéutica</label>
                <input className={inputCls} value={medForm.formaFarmaceutica}
                  onChange={e => setMedForm(p => ({ ...p, formaFarmaceutica: e.target.value }))}
                  placeholder="ej. Tableta" />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Concentración</label>
                <input className={inputCls} value={medForm.concentracion}
                  onChange={e => setMedForm(p => ({ ...p, concentracion: e.target.value }))}
                  placeholder="ej. 400mg" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-text-secondary mb-1">Vía de administración</label>
              <input className={inputCls} value={medForm.viaAdministracion}
                onChange={e => setMedForm(p => ({ ...p, viaAdministracion: e.target.value }))}
                placeholder="ej. Oral, IV, IM..." />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Dosis</label>
                <ComboboxInput options={DOSIS_RAPIDAS} value={medForm.dosis}
                  onChange={v => setMedForm(p => ({ ...p, dosis: v }))}
                  placeholder="ej. 1 tableta" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Frecuencia</label>
                <ComboboxInput options={FRECUENCIAS} value={medForm.frecuencia}
                  onChange={v => setMedForm(p => ({ ...p, frecuencia: v }))}
                  placeholder="ej. cada 8h" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Duración</label>
                <ComboboxInput options={DURACIONES} value={medForm.duracion}
                  onChange={v => setMedForm(p => ({ ...p, duracion: v }))}
                  placeholder="ej. 7 días" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="block text-xs text-text-secondary mb-1">Indicaciones específicas</label>
              <input className={inputCls} value={medForm.indicaciones}
                onChange={e => setMedForm(p => ({ ...p, indicaciones: e.target.value }))} placeholder="Tomar con alimentos, no combinar con..." />
            </div>
            <button type="button" onClick={addMed}
              className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors">
              <Plus size={13} /> Agregar fármaco
            </button>
          </div>

          {/* List */}
          {form.medicaciones.length === 0 ? (
            <p className="text-center text-sm text-text-secondary py-4">Aún no has agregado fármacos</p>
          ) : (
            <div className="space-y-2">
              {form.medicaciones.map((m, i) => (
                <div key={i} className="flex items-start gap-3 p-3.5 border border-gray-100 rounded-xl bg-white">
                  <div className="w-6 h-6 rounded-full bg-primary-50 border border-primary-200 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-primary-700">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="font-semibold text-text-primary text-sm">{m.denominacionGenerica}</p>
                      {m.denominacionDistintiva && m.denominacionDistintiva !== m.denominacionGenerica && (
                        <span className="text-xs text-text-secondary">({m.denominacionDistintiva})</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-2 mt-0.5">
                      {m.formaFarmaceutica && <span className="text-[11px] bg-gray-100 text-text-secondary px-1.5 py-0.5 rounded capitalize">{m.formaFarmaceutica.toLowerCase()}</span>}
                      {m.concentracion && <span className="text-[11px] bg-gray-100 text-text-secondary px-1.5 py-0.5 rounded">{m.concentracion}</span>}
                      {m.viaAdministracion && <span className="text-[11px] bg-gray-100 text-text-secondary px-1.5 py-0.5 rounded capitalize">{m.viaAdministracion.toLowerCase()}</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-3 mt-0.5">
                      {m.dosis      && <span className="text-xs text-text-secondary">{m.dosis}</span>}
                      {m.frecuencia && <span className="text-xs text-text-secondary">{m.frecuencia}</span>}
                      {m.duracion   && <span className="text-xs text-text-secondary">{m.duracion}</span>}
                    </div>
                    {m.indicaciones && <p className="text-xs text-text-secondary italic mt-0.5">{m.indicaciones}</p>}
                  </div>
                  <button type="button" onClick={() => removeMed(i)}
                    className="p-1 rounded-lg hover:bg-red-50 text-text-secondary hover:text-red-500 transition-colors flex-shrink-0">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      );

      case 'med-activos': return (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
            <p className="text-xs text-blue-700">
              Los <strong>{form.medicaciones.length}</strong> fármaco{form.medicaciones.length !== 1 ? 's' : ''} de esta receta se agregarán automáticamente como medicamentos activos.
              Indica cuáles de los medicamentos previos deben <strong>dejarse de tomar</strong>:
            </p>
          </div>

          {medActivos.filter((m: any) => m.activo).length === 0 ? (
            <p className="text-center text-sm text-text-secondary py-4">Sin medicamentos activos previos</p>
          ) : (
            <div className="space-y-2">
              {medActivos.filter((m: any) => m.activo).map((m: any) => {
                const seInactiva = form.medActivosInactivar.has(m.id);
                return (
                  <div key={m.id} onClick={() => toggleInactivar(m.id)}
                    className={cn('flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all select-none',
                      seInactiva ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-white hover:border-gray-200'
                    )}>
                    <button type="button" className="flex-shrink-0 text-text-secondary">
                      {seInactiva
                        ? <ToggleLeft size={20} className="text-red-400" />
                        : <ToggleRight size={20} className="text-green-500" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm font-medium', seInactiva ? 'line-through text-text-secondary' : 'text-text-primary')}>
                        {m.nombreMedicamento}
                      </p>
                      <div className="flex gap-2">
                        {m.dosis      && <span className="text-xs text-text-secondary">{m.dosis}</span>}
                        {m.frecuencia && <span className="text-xs text-text-secondary">{m.frecuencia}</span>}
                      </div>
                    </div>
                    <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border',
                      seInactiva ? 'bg-red-100 text-red-600 border-red-200' : 'bg-green-50 text-green-700 border-green-200'
                    )}>
                      {seInactiva ? 'Dejar de tomar' : 'Continúa'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );

      case 'resumen': return (
        <div className="space-y-5">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-primary-600">{form.medicaciones.length}</p>
              <p className="text-xs text-text-secondary mt-0.5">fármaco{form.medicaciones.length !== 1 ? 's' : ''} nuevo{form.medicaciones.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-red-500">{form.medActivosInactivar.size}</p>
              <p className="text-xs text-text-secondary mt-0.5">medicamento{form.medActivosInactivar.size !== 1 ? 's' : ''} a suspender</p>
            </div>
            <div className="bg-green-50 border border-green-100 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-green-600">
                {medActivos.filter((m: any) => m.activo && !form.medActivosInactivar.has(m.id)).length + form.medicaciones.length}
              </p>
              <p className="text-xs text-text-secondary mt-0.5">total activos al emitir</p>
            </div>
          </div>

          {/* Template label */}
          <div className="flex items-center justify-between text-xs text-text-secondary bg-gray-50 px-3 py-2 rounded-lg">
            <span>Plantilla: <strong className="text-text-primary">{DISENO_INFO[plantillaCfg.diseno].label}</strong></span>
            <span>{TAMANO_INFO[plantillaCfg.tamanoHoja].label}</span>
          </div>

          {/* Print preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Vista previa</p>
              <button onClick={handlePrint}
                className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-800 transition-colors">
                <Printer size={12} /> Imprimir
              </button>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50 flex justify-center py-4">
              <div style={{ width: previewW, height: previewH, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.12)', borderRadius: 4, position: 'relative' }}>
                <div ref={printRef} style={{ width: pw, height: ph, transform: `scale(${PREVIEW_SCALE})`, transformOrigin: 'top left', pointerEvents: 'none', userSelect: 'none' }}>
                  <TemplateRenderer data={templateData} diseno={plantillaCfg.diseno} width={pw} height={ph} disenoPersonalizadoHtml={plantillaCfg.disenoPersonalizadoHtml} />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  const isSaving   = saveMutation.isPending;
  const isEmitting = activarMutation.isPending;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h1 className="font-semibold text-text-primary text-sm">
            {initialRecetaId ? 'Editar borrador' : 'Nueva receta'} · {toDisplayName(paciente)}
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={saveDraft}
              disabled={isSaving}
              className="flex items-center gap-1.5 text-xs border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 text-text-secondary disabled:opacity-50 transition-colors">
              <FileText size={12} /> {isSaving ? 'Guardando...' : 'Guardar borrador'}
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ── Step indicator ── */}
        <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-0 overflow-x-auto">
            {steps.map((step, i) => {
              const isActive    = i === stepIdx;
              const isCompleted = i < stepIdx;
              return (
                <div key={step} className="flex items-center flex-shrink-0">
                  <button
                    onClick={() => i < stepIdx && setStepIdx(i)}
                    disabled={i >= stepIdx}
                    className={cn('flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-all',
                      isActive    ? 'text-primary-700 bg-primary-50 border border-primary-200' :
                      isCompleted ? 'text-green-700 cursor-pointer hover:bg-green-50'           :
                                    'text-text-secondary'
                    )}>
                    <span className={cn('w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0',
                      isActive    ? 'bg-primary-600 text-white' :
                      isCompleted ? 'bg-green-500 text-white'   :
                                    'bg-gray-200 text-gray-500'
                    )}>
                      {isCompleted ? <Check size={9} /> : i + 1}
                    </span>
                    {STEP_LABEL[step]}
                  </button>
                  {i < steps.length - 1 && (
                    <ChevronRight size={13} className="mx-1 text-gray-300 flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <h2 className="text-base font-semibold text-text-primary mb-4">{STEP_LABEL[currentStep]}</h2>
          {renderStep()}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-t border-gray-100 bg-white">
          <button onClick={prev} disabled={stepIdx === 0}
            className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors disabled:opacity-30">
            <ArrowLeft size={14} /> Anterior
          </button>

          {isLast ? (
            <div className="flex items-center gap-3">
              <button onClick={saveDraft} disabled={isSaving}
                className="text-sm border border-gray-200 px-4 py-2 rounded-lg hover:bg-gray-50 text-text-secondary disabled:opacity-50 transition-colors">
                Guardar borrador
              </button>
              <button onClick={handleEmitir} disabled={isSaving || isEmitting}
                className="flex items-center gap-1.5 text-sm bg-primary-600 text-white px-5 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors font-medium">
                {isEmitting ? 'Emitiendo...' : 'Emitir receta'}
              </button>
            </div>
          ) : (
            <button onClick={next}
              className="flex items-center gap-1.5 text-sm bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium">
              Siguiente <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
